import HttpService from '@frontend/AppSettings/services/HttpService';
import {
  pathTimesheetTimeRequestAction,
  pathTimesheetTimeRequestRecipients,
  pathTimesheetTimeRequests,
} from '@frontend/__settings__/septSoftwarePaths';

/**
 * Time requests on locked timesheet days (plans/todo/timesheet-day-locks.md, B3). The requester is
 * always the signed-in user (taken from the token server-side); no employee id is ever sent.
 * Errors are thrown as `HttpError` (status + the server's `data.error` sentence).
 */
export type TimeRequestStatus = 'pending' | 'approved' | 'declined' | 'cancelled';

export interface TimeRequest {
  requestId: string;
  employeeId: string;
  employeeName: string | null;
  matterId: string;
  matterName: string | null;
  costCode: string;
  costCodeLabel: string | null;
  /** yyyy-MM-dd */
  workDate: string;
  /** The cell's total when the request was made. */
  currentHours: number;
  /** The NEW TOTAL asked for (not an amount to add). */
  requestedHours: number;
  note: string | null;
  status: TimeRequestStatus;
  approvedHours: number | null;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
  /** Pending only: the cell's total right now (for "changed since request"). */
  liveHours?: number;
}

export interface TimeRequestFilters {
  status?: TimeRequestStatus;
  from?: string;
  to?: string;
  matterid?: string;
}

export interface CreateTimeRequestBody {
  matterid: string;
  cost_code: string;
  work_date: string;
  hours: number;
  note?: string;
}

/** An administrator who can receive the new-request email (permish 1, an email, can sign in). */
export interface TimeRequestRecipientAdmin {
  employeeId: string;
  name: string;
  email: string;
}

/**
 * Who gets the new-request email (plans/todo/time-request-email-recipients.md). `recipients` is
 * `null` for the default — every administrator in `admins` — otherwise the chosen employee ids
 * (an id no longer in `admins` is ignored when sending).
 */
export interface TimeRequestRecipients {
  recipients: string[] | null;
  admins: TimeRequestRecipientAdmin[];
}

interface Envelope<T> {
  successful: boolean;
  data: T;
}

const http = new HttpService();

class TimeRequestService {
  /** Admins get every request in the firm; anyone else only their own. */
  static async list(filters: TimeRequestFilters = {}): Promise<TimeRequest[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, String(value));
    });
    const query = params.toString();
    const response = await http.get<Envelope<TimeRequest[]>>(
      query ? `${pathTimesheetTimeRequests}?${query}` : pathTimesheetTimeRequests,
    );
    return Array.isArray(response?.data) ? response.data : [];
  }

  static async create(body: CreateTimeRequestBody): Promise<TimeRequest> {
    const response = await http.post<Envelope<TimeRequest>>(pathTimesheetTimeRequests, body);
    return response.data;
  }

  /** Administrators only. `hours` overrides the requested total when the admin adjusted it. */
  static async approve(requestId: string, hours?: number): Promise<TimeRequest> {
    const response = await http.post<Envelope<TimeRequest>>(
      pathTimesheetTimeRequestAction(requestId, 'approve'),
      hours === undefined ? {} : { hours },
    );
    return response.data;
  }

  static async decline(requestId: string, reason?: string): Promise<TimeRequest> {
    const response = await http.post<Envelope<TimeRequest>>(
      pathTimesheetTimeRequestAction(requestId, 'decline'),
      reason ? { reason } : {},
    );
    return response.data;
  }

  /** Administrators only: the saved recipients and the administrators that can be chosen. */
  static async getRecipients(): Promise<TimeRequestRecipients> {
    const response = await http.get<Envelope<TimeRequestRecipients>>(pathTimesheetTimeRequestRecipients);
    return response.data;
  }

  /** Administrators only. `null` resets to every administrator. Answers the saved state. */
  static async setRecipients(ids: string[] | null): Promise<TimeRequestRecipients> {
    const response = await http.put<Envelope<TimeRequestRecipients>>(pathTimesheetTimeRequestRecipients, {
      recipients: ids,
    });
    return response.data;
  }

  /** The requester withdraws their own pending request. */
  static async cancel(requestId: string): Promise<TimeRequest> {
    const response = await http.post<Envelope<TimeRequest>>(
      pathTimesheetTimeRequestAction(requestId, 'cancel'),
    );
    return response.data;
  }
}

export default TimeRequestService;
